<script setup lang="ts">
  import { ref, computed } from "vue";
  import { IconArrowLeft, IconChevronRight, Text, IconClear, Button, IconConnection } from "@ui-kit";
  import { type Product, itemLoadApi } from "@src/api";
  import { TopBar, StatusBar } from "@src/components";
  import { useProfileOptions } from "@src/hooks";
  import { type ShopZoneWithChildren } from "./useProductZoning";
  import { sortZonesByTitle } from "./model";
  import { default as AddProductModal } from "./AddProductModal.vue";
  import { default as ProductZoneItemList } from "./ProductZoneItemList.vue";
  import { default as ProductZoningWipeZoneModal } from "./ProductZoningWipeZoneModal.vue";
  import { CheckBindingItemInZone } from "./ui";

  const { shopZone } = defineProps<{ shopZone: ShopZoneWithChildren }>();
  const emit = defineEmits<{
    "click:back": [value: string];
    "click:zone": [value: ShopZoneWithChildren];
  }>();

  const add_item_modal_visible = ref(false);
  const wipeZoneModalVisible = ref(false);
  const productList = ref<Product[]>([]);
  const is_location = computed(() => !shopZone?.children);

  const check_binding_visible = ref(false);
  const check_binding_params = ref<{
    zone_path: string;
    zone_id: string;
    item_ids: string[];
  } | null>(null);

  const { hasPermission } = useProfileOptions();

  const handleAddZone = (products: Product[]) => {
    productList.value.push(...products);
  };

  const handleDeleteProduct = (product: Product) => {
    const index = productList.value.findIndex((p) => p.item_id === product.item_id);

    if (index !== -1) {
      productList.value.splice(index, 1);
    }
  };

  const handleClickLocator = () => {
    check_binding_params.value = {
      zone_path: shopZone.path,
      zone_id: shopZone.zone_id,
      item_ids: productList.value.map((item) => item.item_id),
    };
    check_binding_visible.value = true;
  };

  const handleCloseCheckBinding = async (payload?: { unlinked_item_ids: string[]; linked_item_ids: string[] }) => {
    if (payload?.unlinked_item_ids?.length) {
      productList.value = productList.value.filter((p) => !payload.unlinked_item_ids.includes(p.item_id));
    }
    if (payload?.linked_item_ids?.length) {
      const res = await itemLoadApi({ item_id: payload.linked_item_ids });
      if (res.code === "OK" && res.items?.length) {
        productList.value.push(...(res.items as Product[]));
      }
    }
    check_binding_visible.value = false;
    check_binding_params.value = null;
  };

  const sortedZones = computed(() => {
    return shopZone?.children ? sortZonesByTitle(shopZone.children) : [];
  });

  const available_add_items = computed(() => hasPermission("mobile.item_set_zone"));
</script>

<template>
  <div class="product-zoning-list" data-testid="product-zoning-list">
    <StatusBar />
    <TopBar
      :title="shopZone.path"
      :subtitle="`${$t('Товаров в зоне')}: ${productList.length}`"
      @click:left="emit('click:back', shopZone.parentId)"
      @click:right="
        () => {
          if (hasPermission('mobile.shop_zone_wipe') && productList.length) {
            wipeZoneModalVisible = true;
          }
        }
      "
      data-testid="zoning-zone-top-bar"
    >
      <template #left>
        <IconArrowLeft />
      </template>
      <template #right v-if="is_location && hasPermission('mobile.shop_zone_wipe') && productList.length">
        <IconClear />
      </template>
    </TopBar>
    <div class="product-zoning-list__content" data-testid="product-zoning-list-content">
      <ProductZoneItemList
        v-if="is_location"
        :productList="productList"
        :shopZone="shopZone"
        @click:delete="handleDeleteProduct"
      />
      <div
        v-else
        v-for="zone in sortedZones"
        :key="zone?.zone_id"
        class="product-zoning-list__content-item"
        @click="emit('click:zone', zone)"
        data-testid="product-zoning-list-item"
      >
        <div class="product-zoning-list__content-item-text">
          <Text font="body-medium-16">{{ zone.title }}</Text>
        </div>
        <IconChevronRight />
      </div>
    </div>
    <div v-if="is_location" class="product-zoning-list__content-footer">
      <Button v-if="available_add_items" @click="add_item_modal_visible = true" data-testid="add-product-btn">
        {{ $t("Добавить товары") }}
      </Button>
      <Button
        variant="secondary"
        :asSquare="available_add_items"
        @click="handleClickLocator"
        data-testid="check-binding-item-in-zone-btn"
      >
        <template #iconBefore>
          <IconConnection />
        </template>
        {{ !available_add_items ? $t("Проверка привязки") : undefined }}
      </Button>
    </div>
  </div>
  <ProductZoningWipeZoneModal
    :zoneId="shopZone?.zone_id"
    @click:close="wipeZoneModalVisible = false"
    @click:delete="productList.splice(0, productList.length)"
    :visible="wipeZoneModalVisible"
  />
  <AddProductModal
    v-if="add_item_modal_visible"
    :zone_id="shopZone?.zone_id"
    :subtitle="shopZone.path"
    :zone_item_count="productList.length"
    @close="add_item_modal_visible = false"
    @add:zone="handleAddZone"
  />
  <CheckBindingItemInZone
    v-if="check_binding_visible"
    :zone_path="check_binding_params?.zone_path ?? null"
    :zone_id="check_binding_params?.zone_id ?? ''"
    :item_ids="check_binding_params?.item_ids ?? []"
    @close="handleCloseCheckBinding"
  />
</template>

<style scoped>
  .product-zoning-list {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  .product-zoning-list :deep(.tabs .tab) {
    flex: 1;
  }

  .product-zoning-list__content {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 0px 16px 16px;
    gap: 8px;
    overflow-y: auto;
  }

  .product-zoning-list__content-item {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-radius: 16px;
    background-color: var(--sf-base-background-white);
  }

  .product-zoning-list__content-footer {
    padding: 16px;
    background-color: var(--sf-base-background-white);
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }

  .product-zoning-list__content-footer {
    display: flex;
    gap: 8px;

    & :deep(button:first-of-type) {
      flex: 1;
    }

    & :deep(button:last-of-type) {
      margin-left: auto;
    }
  }
</style>
